"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, PhoneCall, ArrowRight, EyeOff, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const relatedServices = [{
  title: "Sewage Cleanup",
  to: "/SewageCleanupPage",
  badge: "Bio-Contaminant Category 3",
  description: "Rapid decontamination, black water extraction, and antimicrobial sanitization to restore sanitary baseline safety.",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_cleanup_containment.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_cleanup_containment.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_cleanup_containment.png-wvc-sizes",
  alt: "Technician operating commercial sewage extraction and sanitizing equipment"
}, {
  title: "Asbestos & Lead Abatement",
  to: "/AsbestosLeadAbatementPage",
  badge: "Certified Hazard Mitigation",
  description: "Negative air containment, certified environmental testing, and safe regulated material removal across older structures.",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/asbestos_containment_abatement.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/asbestos_containment_abatement.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/asbestos_containment_abatement.png-wvc-sizes",
  alt: "Field crew member sealing an environmental containment barrier under negative pressure"
}];
const assurancePoints = [{
  icon: EyeOff,
  title: "Unbranded vehicles upon request",
  detail: "Preserving your privacy in sensitive neighborhoods and commercial environments."
}, {
  icon: ShieldCheck,
  title: "Direct TriServe personnel only",
  detail: "No third-party call centers or brokers. You speak straight to the field leads on site."
}, {
  icon: FileText,
  title: "Direct insurance coordination",
  detail: "Itemized photo dossiers and certified chain-of-custody documentation for smooth adjuster processing."
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Deep Purple Structural Command Container */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground border border-border/20 p-6 sm:p-8 md:p-12 shadow-2xl relative overflow-hidden">
          {/* Subtle Ambient Texture / Hairline Decorative Spec Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-8 border-b border-secondary-foreground/15 text-xs font-mono tracking-wider uppercase text-secondary-foreground/80">
            <span className="inline-flex items-center gap-2">
              <span className="size-2 rounded-full bg-accent animate-pulse" />
              Direct Field Dispatch • MD / DC / NOVA
            </span>
            <span>TriServe Biohazard Protocol #42-B</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Technician Photographic Frame */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.25
          }} variants={fadeUp} className="lg:col-span-5">
              <div className="relative rounded-xl overflow-hidden border border-secondary-foreground/20 bg-background/5 shadow-xl group">
                <div className="aspect-[4/5] sm:aspect-[16/11] lg:aspect-[4/5] relative">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/biohazard_containment_technician.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/biohazard_containment_technician.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/biohazard_containment_technician.png-wvc-sizes" alt="TriServe certified biohazard technician methodically taping a sealed plastic barrier" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                </div>

                <div className="absolute bottom-4 left-4 right-4 p-3 rounded-lg bg-background/90 text-foreground border border-border text-xs backdrop-blur-sm">
                  <div className="flex items-center justify-between font-mono">
                    <span className="font-semibold flex items-center gap-1.5">
                      <CheckCircle2 className="size-3.5 text-primary" /> Active Containment Barrier
                    </span>
                    <span className="text-muted-foreground">Level 4 PPE</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content & Value Proposition */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.25
          }} variants={fadeUp} className="lg:col-span-7 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent font-mono text-xs uppercase tracking-wide w-fit mb-4 border border-accent/30">
                Discreet Field Operations
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-secondary-foreground mb-4">
                Local technicians with complete privacy
              </h2>

              <p className="text-base sm:text-lg text-secondary-foreground/90 leading-relaxed mb-6 font-default">
                Biohazard decontamination, trauma scenes, and biological hazards require technical rigor combined with quiet respect for affected families and facility teams. TriServe deploys its own vetted local specialists who manage containment directly—with unbranded response vehicles available on request and direct coordination with your insurance adjuster throughout.
              </p>

              {/* Three Operational Assurances */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 mb-8 border-t border-secondary-foreground/15">
                {assurancePoints.map((point, index) => {
                const Icon = point.icon;
                return <div data-index={index} key={point.title} className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 text-accent font-semibold text-sm">
                        <Icon className="size-4 shrink-0" />
                        <span>{point.title}</span>
                      </div>
                      <p className="text-xs text-secondary-foreground/75 leading-relaxed">
                        {point.detail}
                      </p>
                    </div>;
              })}
              </div>

              {/* Immediate Call Trigger */}
              <div className="flex flex-wrap items-center gap-4">
                <Button asChild className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 px-6 py-2.5 h-auto transition-[background-color,box-shadow]">
                  <a href="tel:855-435-6631" className="inline-flex items-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>Call 855-435-6631</span>
                  </a>
                </Button>
                <span className="text-xs text-secondary-foreground/70 font-mono">
                  24/7 confidential dispatch • Zero third-party call centers
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Related Services Sub-Section */}
        <div className="mt-16 sm:mt-20">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 pb-4 border-b border-border gap-4">
            <div>
              <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold block mb-1">
                Integrated Restoration
              </span>
              <h3 className="text-xl sm:text-2xl font-bold font-default tracking-tight text-foreground">
                Related containment & recovery services
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md font-default">
              Disaster scenes frequently involve layered environmental requirements. Our crews transition directly between mitigation and abatement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {relatedServices.map((service, index) => <motion.div data-index={index} key={service.title} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeUp}>
                <Card className="h-full bg-card text-card-foreground border-border flex flex-col justify-between overflow-hidden group hover:border-primary transition-[border-color,box-shadow] duration-200">
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
                    <img src={service.imgSrc} data-wvc-srcset={service.imgSrcset} data-wvc-sizes={service.imgSizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-[transform] duration-300 group-hover:scale-105" />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-mono font-medium rounded bg-background/90 text-foreground border border-border/80 backdrop-blur-sm">
                        {service.badge}
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-5 pb-2">
                    <CardTitle className="text-xl font-bold font-default group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="text-xs font-mono text-muted-foreground flex items-center gap-2">
                      <span className="size-1.5 rounded-full bg-primary" />
                      Standard protocol throughout MD, DC & NoVA
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 pb-5 border-t border-border/60">
                    <Button asChild variant="ghost" className="w-full justify-between px-0 hover:bg-transparent text-primary hover:text-primary/80 font-semibold">
                      <Link to={service.to} className="inline-flex items-center w-full justify-between">
                        <span>View service specifications</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}